// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { makeAutoObservable } from "mobx";

import { SimpleNavLinkProps } from "~@jii/common-ui";
import { ResidentFlags, UserStore } from "~@jii/data";
import { RouteParams, State } from "~@jii/paths";
import { I18nResources } from "~@jii/translation";

type AdditionalTopBarLink = {
  navKey: keyof I18nResources["common"]["nav"];
  to: string;
  // Whether this link should stay highlighted while on any of its sub-routes,
  // rather than only when its own path is matched exactly. Defaults to false.
  activeOnSubroutes?: boolean;
  // Only for side effects (e.g. analytics) - the link should navigate with `to`.
  onClick?: () => void;
};

export class ResidentNavBarPresenter {
  constructor(
    private userStore: UserStore,
    private routeParams:
      | RouteParams<typeof State.Resident>
      | RouteParams<typeof State>,
    private residentFlags?: ResidentFlags,
  ) {
    makeAutoObservable(this);
  }

  get homeLink() {
    if (!("personPseudoId" in this.routeParams)) return;

    return {
      to: State.Resident.buildPath(this.routeParams),
      end: true,
    };
  }

  // TODO(#10032): [JII][P2] Parameterize additional top-level links in ResidentNavBar
  get additionalTopBarLinks(): AdditionalTopBarLink[] {
    if (!("personPseudoId" in this.routeParams)) return [];

    if (this.routeParams.stateSlug === "tennessee") {
      return [
        {
          navKey: "aboutLink",
          to: State.Resident.UsTnMoreInformation.About.buildPath(
            this.routeParams,
          ),
        },
      ];
    }

    if (this.routeParams.stateSlug === "arizona") {
      const links: Array<AdditionalTopBarLink> = [
        {
          navKey: "aboutLink",
          to: State.Resident.UsAzMoreInformation.About.buildPath(
            this.routeParams,
          ),
        },
      ];
      if (this.residentFlags?.usAzClassification) {
        links.push({
          navKey: "myClassificationLink",
          to: State.Resident.UsAzClassificationPoints.buildPath(
            this.routeParams,
          ),
          activeOnSubroutes: true,
        });
      }
      return links;
    }

    const showsProgramCatalog =
      this.routeParams.stateSlug === "arkansas" ||
      this.routeParams.stateSlug === "colorado";

    if (showsProgramCatalog) {
      return [
        {
          navKey: "programsLink",
          to: State.Resident.ProgramCatalog.buildPath(this.routeParams),
          activeOnSubroutes: true,
        },
      ];
    }

    if (this.routeParams.stateSlug === "mass") {
      const links: AdditionalTopBarLink[] = [
        {
          navKey: "programsLink",
          to: State.Resident.ProgramCatalog.buildPath(this.routeParams),
        },
      ];

      if (this.residentFlags?.usMaReentry) {
        const { personPseudoId } = this.routeParams;
        links.push({
          navKey: "reentryLink",
          to: State.Resident.UsMaReentry.buildPath(this.routeParams),
          activeOnSubroutes: true,
          onClick: () =>
            this.userStore.segmentClient.trackReentryEvent(
              "frontend_reentry_entrypoint_clicked",
              {
                justiceInvolvedPersonPseudoId: personPseudoId,
                entryPoint: "global_nav",
              },
            ),
        });
      }

      return links;
    }

    return [];
  }

  get menuLinks(): Array<SimpleNavLinkProps> {
    const links: Array<SimpleNavLinkProps> = [];

    if (this.userStore.hasPermission("enhanced")) {
      links.push({
        children: "Search",
        to: State.Search.buildPath({
          stateSlug: this.routeParams.stateSlug,
        }),
        end: true,
      });
    }

    return links;
  }
}
